'use client';

import Link from '@eliadmin/core/src/components/link';

import type { Blog } from '@eliadmin/core/src/api/blogs/blogs-types';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Heading } from '../ui/heading';
import { Button } from '../ui/button';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

import { PiArrowCircleRight } from 'react-icons/pi';
import { Carousel } from '@eliadmin/core/src/components/ui/carousel';
import { BlogCard } from '@eliadmin/core/src/components/blog/blog-card';
import { useCarouselContext } from '@eliadmin/core/src/components/ui/carousel-context';
import { CarouselProgress } from '../ui/carousel-progress';
import { CarouselControls } from '@eliadmin/core/src/components/ui/carousel-controls';

export function Blogs({
  blogs,
  title,
  className,
}: {
  blogs: Blog[];
  title: string;
  className?: { section?: string; container?: string };
}) {
  const { t } = useTranslations();

  const options = {
    align: 'start' as const,
  };

  const carouselContext = useCarouselContext();

  if (!carouselContext) {
    throw new Error('Hero must be used within a CarouselProvider');
  }

  const { selectedIndex } = carouselContext;

  return (
    <section
      className={cn(
        'custom-section',
        'gap-6 bg-[#F2EDE6] pb-[50px] pr-0 pt-6 sm:gap-8 sm:pb-16 sm:pr-4 sm:pt-10 lg:gap-10 lg:pb-[100px] lg:pt-[50px]',
        className?.section,
      )}
    >
      <div
        className={cn(
          'custom-container',
          'flex items-center justify-center sm:justify-between',
          className?.container,
        )}
      >
        <Heading title={title} />
        <Button asChild className="hidden whitespace-nowrap sm:flex">
          <Link prefetch={false} href={ROUTES.BLOG()}>
            {t('text2000')} <PiArrowCircleRight size={24} />
          </Link>
        </Button>
      </div>
      <div className={cn('custom-container')}>
        <Carousel
          options={options}
          className={{
            container: 'w-full gap-4 sm:gap-5 lg:gap-6',
            main: 'w-full',
            wrapper: 'w-full',
            slide: 'w-full max-w-[300px] sm:max-w-[360px] lg:max-w-[427px]',
          }}
          slides={blogs.map((blog, index) => (
            <BlogCard blog={blog} key={index} />
          ))}
        />
      </div>
      <div
        className={cn('custom-container', 'flex min-h-[52px] items-center justify-between gap-4 pr-4 sm:hidden')}
      >
        <CarouselProgress selectedIndex={selectedIndex} totalSlides={blogs.length} />
        <CarouselControls
          buttonProps={{ size: 'icon-lg', rounded: 'md', variant: 'secondary' }}
          className={{
            main: 'flex items-center gap-2',
            button: 'bg-[#D3C7B8] text-foreground shadow-none',
          }}
        />
      </div>
      <div className="flex w-full items-center justify-center pr-4 sm:hidden">
        <Button asChild>
          <Link prefetch={false} href={ROUTES.BLOG()}>
            {t('text2000')} <PiArrowCircleRight size={24} />
          </Link>
        </Button>
      </div>
    </section>
  );
}
